import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { revokeMember } from '@/api/member';
import type { MemberAdminDetailResp } from '@shared/api.interface';
import { MEMBER_STATUS_LABELS } from '@shared/api.interface';
import { extractApiErrorMessage } from './api-error';

const REASONS = ['退款', '码泄露', '重复发货', '其他'];
const NOTE_MAX_LENGTH = 150;

/** 原因 + 选填说明合成一条记录，例如“退款：客户说孩子不需要了” */
export function buildRevokeReason(reason: string, note: string): string {
  const detail = note.trim().replace(/\s+/g, ' ');
  return detail ? `${reason}：${detail}` : reason;
}

interface RevokeMemberDialogProps {
  detail: MemberAdminDetailResp | null;
  onClose: () => void;
  onRevoked: () => Promise<void> | void;
}

export function RevokeMemberDialog({ detail, onClose, onRevoked }: RevokeMemberDialogProps) {
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (detail) {
      setReason(REASONS[0]);
      setNote('');
    }
  }, [detail]);

  const member = detail?.member;
  const claimed = (detail?.recent_records ?? []).filter(record => record.record_type !== 'quota_adjustment' && record.result === 'success');
  const remaining = member
    ? member.quota_mode === 'total'
      ? Math.max(0, (member.total_quota ?? 0) - member.total_redemptions)
      : null
    : null;

  const handleConfirm = async () => {
    if (!member || isSaving) return;
    setIsSaving(true);
    try {
      const fullReason = buildRevokeReason(reason, note);
      await revokeMember({ passcode: member.passcode, reason: fullReason });
      toast.success(`已作废，原因：${fullReason}`);
      onClose();
      await onRevoked();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '作废失败'));
    } finally {
      setIsSaving(false);
    }
  };

  const stats = member ? [
    { label: '状态', value: MEMBER_STATUS_LABELS[member.status] },
    { label: '已领主题', value: `${claimed.length} 个` },
    { label: '剩余', value: remaining !== null ? `${remaining} 次` : '按月' },
  ] : [];

  return (
    <Dialog open={!!detail} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-md gap-4 overflow-y-auto rounded-2xl">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-xl">作废兑换码</DialogTitle>
          <DialogDescription className="break-all">
            <span className="font-mono text-foreground">{member?.passcode}</span>
            {member?.member_type ? ` · ${member.member_type}` : ''}
          </DialogDescription>
        </DialogHeader>
        {member && (
          <div className="min-w-0 space-y-4 text-sm">
            <div className="grid grid-cols-3 divide-x rounded-xl bg-white text-center ring-1 ring-border">
              {stats.map(item => (
                <div key={item.label} className="min-w-0 px-2 py-2.5">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="mt-0.5 font-semibold tabular-nums">{item.value}</p>
                </div>
              ))}
            </div>

            {claimed.length > 0 && (
              <div role="alert" className="space-y-2 rounded-xl bg-amber-50 px-3 py-2.5 text-amber-900">
                <p>
                  <strong className="font-semibold">已领的网盘链接收不回来。</strong>
                  是否同意退款请你自己判断，作废只能让这个码以后不能再用。
                </p>
                <ul className="space-y-1 text-[13px]">
                  {claimed.map(record => (
                    <li key={record.id} className="flex min-w-0 justify-between gap-3">
                      <span className="min-w-0 truncate">{record.topic_name}</span>
                      <span className="shrink-0 text-amber-800/80">{new Date(record.redemption_time).toLocaleDateString('zh-CN')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <fieldset className="space-y-2">
              <legend className="mb-2 font-medium">作废原因</legend>
              <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="作废原因">
                {REASONS.map(item => {
                  const active = item === reason;
                  return (
                    <button
                      key={item}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setReason(item)}
                      className={`h-10 rounded-full border px-1 text-[13px] transition-colors duration-150 ease-out ${active ? 'border-red-300 bg-red-50 font-semibold text-red-700' : 'border-border bg-white text-foreground hover:bg-accent'}`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor="revoke-note" className="font-medium">补充说明（选填）</label>
                <span className="text-xs tabular-nums text-muted-foreground">{note.length}/{NOTE_MAX_LENGTH}</span>
              </div>
              <Textarea
                id="revoke-note"
                rows={2}
                maxLength={NOTE_MAX_LENGTH}
                placeholder="例如：客户说孩子不需要了，已退款"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="min-h-[72px] rounded-xl"
              />
            </div>

            <p className="text-xs leading-relaxed text-muted-foreground">
              作废后不能恢复，用户再输入会看到“兑换码已失效”。要补偿用户，请重新生成一个码。
            </p>
          </div>
        )}
        <div className="grid grid-cols-[1fr_2fr] gap-2.5">
          <Button variant="outline" onClick={onClose} disabled={isSaving} className="h-12 rounded-xl font-semibold">
            取消
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isSaving} className="h-12 rounded-xl text-base font-semibold">
            {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            确认作废
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
